const renderers = new WeakMap();

const VERTEX = `
attribute vec2 a_pos;
attribute vec2 a_tex;
varying vec2 v_tex;
void main() {
  gl_Position = vec4(a_pos, 0.0, 1.0);
  v_tex = a_tex;
}`;

const FRAGMENT = `
precision mediump float;
varying vec2 v_tex;
uniform sampler2D u_texY;
uniform sampler2D u_texCb;
uniform sampler2D u_texCr;
uniform float u_fullRange;
void main() {
  float yRaw = texture2D(u_texY, v_tex).r;
  float yLimited = (yRaw - 0.062745098) * 1.164383562;
  float y = mix(yLimited, yRaw, u_fullRange);
  float cb = texture2D(u_texCb, v_tex).r - 0.5;
  float cr = texture2D(u_texCr, v_tex).r - 0.5;
  gl_FragColor = vec4(
    clamp(y + 1.792741 * cr, 0.0, 1.0),
    clamp(y - 0.213249 * cb - 0.532909 * cr, 0.0, 1.0),
    clamp(y + 2.112402 * cb, 0.0, 1.0),
    1.0
  );
}`;

function sampleShift(planes, bitDepth) {
  if (bitDepth === 10 || bitDepth === 12) return bitDepth - 8;
  let max = 0;
  planes.forEach((plane) => {
    const step = Math.max(1, Math.floor(plane.length / 64));
    for (let index = 0; index < plane.length; index += step) {
      if (plane[index] > max) max = plane[index];
    }
  });
  let shift = 0;
  while (max >> shift > 255 && shift < 8) shift += 1;
  return shift;
}

function chromaIsEmpty(plane, shift) {
  const step = Math.max(1, Math.floor(plane.length / 48));
  let sum = 0;
  let count = 0;
  for (let index = 0; index < plane.length; index += step) {
    sum += plane[index] >> shift;
    count += 1;
  }
  return count > 0 && sum / count < 12;
}

function planeBytes(data, shift) {
  const bytes = new Uint8Array(data.length);
  for (let index = 0; index < data.length; index += 1) {
    const value = data[index] >> shift;
    bytes[index] = value > 255 ? 255 : value;
  }
  return bytes;
}

function shader(gl, type, source) {
  const item = gl.createShader(type);
  gl.shaderSource(item, source);
  gl.compileShader(item);
  return item;
}

function texture(gl, filter) {
  const item = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, item);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return item;
}

function createRenderer(canvas) {
  const gl = canvas.getContext("webgl", { preserveDrawingBuffer: true, alpha: false });
  if (!gl) return null;
  const program = gl.createProgram();
  gl.attachShader(program, shader(gl, gl.VERTEX_SHADER, VERTEX));
  gl.attachShader(program, shader(gl, gl.FRAGMENT_SHADER, FRAGMENT));
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  gl.useProgram(program);

  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 0, 1, 1, -1, 1, 1, -1, 1, 0, 0, 1, 1, 1, 0]),
    gl.STATIC_DRAW
  );
  const position = gl.getAttribLocation(program, "a_pos");
  const texCoord = gl.getAttribLocation(program, "a_tex");
  gl.enableVertexAttribArray(position);
  gl.enableVertexAttribArray(texCoord);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 16, 0);
  gl.vertexAttribPointer(texCoord, 2, gl.FLOAT, false, 16, 8);

  const planes = [texture(gl, gl.LINEAR), texture(gl, gl.NEAREST), texture(gl, gl.NEAREST)];
  gl.uniform1i(gl.getUniformLocation(program, "u_texY"), 0);
  gl.uniform1i(gl.getUniformLocation(program, "u_texCb"), 1);
  gl.uniform1i(gl.getUniformLocation(program, "u_texCr"), 2);
  const fullRange = gl.getUniformLocation(program, "u_fullRange");
  gl.uniform1f(fullRange, 0);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  return { gl, planes, fullRange };
}

function upload(gl, tex, unit, bytes, width, height) {
  gl.activeTexture(gl.TEXTURE0 + unit);
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, width, height, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, bytes);
}

function contain(sourceWidth, sourceHeight, boxWidth, boxHeight) {
  const scale = Math.min(boxWidth / sourceWidth, boxHeight / sourceHeight);
  const width = Math.round(sourceWidth * scale);
  const height = Math.round(sourceHeight * scale);
  return {
    x: Math.round((boxWidth - width) / 2),
    y: Math.round((boxHeight - height) / 2),
    width: Math.max(1, width),
    height: Math.max(1, height),
  };
}

export function drawYuvFrame(canvas, frame) {
  if (!canvas || !frame?.width || !frame?.height || !frame.y || !frame.cb || !frame.cr) return false;
  const shift = sampleShift([frame.y, frame.cb, frame.cr], frame.bitDepth || 0);
  if (chromaIsEmpty(frame.cb, shift) || chromaIsEmpty(frame.cr, shift)) return false;
  const { width, height } = frame;
  const chromaWidth = frame.chromaWidth || Math.ceil(width / 2);
  const chromaHeight = frame.chromaHeight || Math.ceil(height / 2);
  let renderer = renderers.get(canvas);
  if (!renderer || renderer.width !== width || renderer.height !== height) {
    const surface = document.createElement("canvas");
    surface.width = width;
    surface.height = height;
    renderer = createRenderer(surface);
    if (!renderer) return false;
    renderer.width = width;
    renderer.height = height;
    renderer.surface = surface;
    renderers.set(canvas, renderer);
  }

  const { gl, planes, surface, fullRange } = renderer;
  gl.viewport(0, 0, width, height);
  gl.uniform1f(fullRange, frame.fullRange ? 1 : 0);
  upload(gl, planes[0], 0, planeBytes(frame.y, shift), width, height);
  upload(gl, planes[1], 1, planeBytes(frame.cb, shift), chromaWidth, chromaHeight);
  upload(gl, planes[2], 2, planeBytes(frame.cr, shift), chromaWidth, chromaHeight);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

  const ratio = window.devicePixelRatio || 1;
  const boxWidth = Math.round((canvas.clientWidth || width) * ratio);
  const boxHeight = Math.round((canvas.clientHeight || height) * ratio);
  if (canvas.width !== boxWidth) canvas.width = boxWidth;
  if (canvas.height !== boxHeight) canvas.height = boxHeight;
  const view = canvas.getContext("2d");
  if (!view) return false;
  const fitted = contain(width, height, boxWidth, boxHeight);
  view.imageSmoothingEnabled = true;
  view.imageSmoothingQuality = "high";
  view.fillStyle = "#000";
  view.fillRect(0, 0, boxWidth, boxHeight);
  view.drawImage(surface, fitted.x, fitted.y, fitted.width, fitted.height);
  return true;
}
